// AGC: Post-wizard welcome page — country-aware promo based on language
import React from 'react'
import { ExternalLink, Play, BookOpen, ArrowRight, Crown, Gift } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'

interface WelcomePageProps {
  onContinue: () => void
  onGoToPlans?: () => void
}

// AGC: Promo codes per language — pt=AgriShow, es=AgroActiva, en=default AgriShow
const PROMO_CODES: Record<string, string> = {
  pt: 'PROMOAGRISHOW',
  es: 'PROMOAGROACTIVA',
  en: 'PROMOAGRISHOW',
}

const WelcomePage: React.FC<WelcomePageProps> = ({ onContinue, onGoToPlans }) => {
  const { t, language } = useTranslation()
  const promoCode = PROMO_CODES[language] ?? PROMO_CODES.en

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex flex-col items-center justify-center px-6 py-12">
      <div className="mb-6">
        <img
          src="/droidfarm-logo.png"
          alt="DroidFarm"
          className="h-16 w-auto"
          onError={(e) => {
            (e.target as HTMLImageElement).style.display = 'none'
          }}
        />
      </div>

      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8 space-y-6">
        <div className="text-center space-y-3">
          <h1 className="text-2xl font-bold text-gray-900">
            {t('wizard.postWizard.title')}
          </h1>
          <p className="text-gray-600 text-sm leading-relaxed">
            {t('wizard.postWizard.description')}
          </p>
        </div>

        <a
          href="https://www.youtube.com/watch?v=rqnlDGBpEak"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 p-4 bg-red-50 hover:bg-red-100 rounded-xl transition-colors group"
        >
          <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
            <Play className="w-6 h-6 text-white ml-0.5" fill="white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{t('wizard.postWizard.tutorial')}</p>
            <p className="text-xs text-gray-500">{t('wizard.postWizard.tutorialDesc')}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-red-500 flex-shrink-0" />
        </a>

        <a
          href="https://www.droidfarm.com/docs"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 p-4 bg-emerald-50 hover:bg-emerald-100 rounded-xl transition-colors group"
        >
          <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{t('wizard.postWizard.helpCenter')}</p>
            <p className="text-xs text-gray-500">{t('wizard.postWizard.helpCenterDesc')}</p>
          </div>
          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-emerald-500 flex-shrink-0" />
        </a>

        {/* AGC: Promo code banner — code varies by language */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Gift className="w-5 h-5 text-amber-600" />
            <span className="font-semibold text-amber-800 text-sm">{t('wizard.postWizard.promoLabel')}</span>
          </div>
          <p className="text-xs text-amber-700">
            {t('wizard.postWizard.promoUseCode')}{' '}
            <span className="font-bold bg-amber-100 px-2 py-0.5 rounded text-amber-900">{promoCode}</span>{' '}
            {t('wizard.postWizard.promoReward')}
          </p>
        </div>

        {onGoToPlans && (
          <button
            onClick={onGoToPlans}
            className="w-full flex items-center justify-center gap-2 py-3.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-base"
          >
            <Crown className="w-5 h-5" />
            {t('wizard.postWizard.viewPlans')}
          </button>
        )}

        <button
          onClick={onContinue}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-xl transition-colors text-base"
        >
          {t('wizard.postWizard.startUsing')}
          <ArrowRight className="w-5 h-5" />
        </button>
      </div>

      <p className="mt-6 text-xs text-gray-400 text-center">
        {t('wizard.postWizard.menuNote')}
      </p>
    </div>
  )
}

export { WelcomePage }
export default WelcomePage
